import { useConstructionProjectSuspenseQuery } from '@/clients/constructionProject/useConstructionProject';
import { useDealSuspenseQuery } from '@/clients/deal/useDeal';
import { useOrganizationSuspenseQuery } from '@/clients/organization/useOrganization';
import { ClientInformationSummary } from '@/shared/components/ClientInformationSummary';
import { SectionSummary } from '@/shared/components/SectionSummary';
import { Button, MessageBox } from '@schuettflix/react-components';
import { useTranslation } from 'react-i18next';
import { DealLineItemsTable } from './DealLineItemsTable';
import { BusinessCase, PRICING_STRATEGY, PRODUCT_TYPES } from '@schuettflix/interfaces';
import { useMemo, useState } from 'react';
import { BusinessCasePreview } from './BusinessCasePreview';
import { useNavigate } from 'react-router';
import { getProductConfigurationsFromDealLineItems } from '../utils/getProductConfigurationsFromDealLineItems';
import { useFormattedActiveTaxClassesByMarket } from '@/clients/tax-classes/useTaxClasses';
import { useMarketContext } from '@/shared/context/MarketContext';

type SalesforceDealImporterProps = {
    opportunityId: string;
    constructionProjectId: string;
};
export function SalesforceDealImporter({ constructionProjectId, opportunityId }: SalesforceDealImporterProps) {
    const { t } = useTranslation();
    const { marketCode } = useMarketContext();
    const navigate = useNavigate();
    const taxInfo = useFormattedActiveTaxClassesByMarket();
    const { data: constructionProject } = useConstructionProjectSuspenseQuery(constructionProjectId);
    const { data: customerOrganization } = useOrganizationSuspenseQuery(constructionProject?.organizationId);
    const { data: deal } = useDealSuspenseQuery(opportunityId);
    const [selection, setSelection] = useState<{
        selectedDealLineItemIds: string[];
        lineItemIdsWithDeselectedRemarks: string[];
        manualSelectedProductTypesMap: Map<string, PRODUCT_TYPES>;
        mainPricingStrategy: PRICING_STRATEGY | null;
    }>({
        selectedDealLineItemIds: [],
        lineItemIdsWithDeselectedRemarks: [],
        manualSelectedProductTypesMap: new Map(),
        mainPricingStrategy: 'STANDARD',
    });

    const selectedDealLineItems = useMemo(() => {
        if (!deal) {
            return [];
        }
        return selection.selectedDealLineItemIds.flatMap(lineItemId => {
            const dealLineItem = deal.deal.lineItems.find(
                lineItem => lineItem.salesforceMetaData.opportunityLineItemId === lineItemId
            );

            if (!dealLineItem) {
                return [];
            }

            if (dealLineItem.product.productTypeConfig.mappingStrategy === 'MANUAL') {
                dealLineItem.product.productType = selection.manualSelectedProductTypesMap.get(lineItemId) ?? null;
            }

            return dealLineItem;
        });
    }, [selection.selectedDealLineItemIds, deal, selection.manualSelectedProductTypesMap]);

    const productConfigurations = useMemo(() => {
        if (!taxInfo.defaultTaxClass || !deal) {
            return [];
        }

        return getProductConfigurationsFromDealLineItems({
            dealLineItems: selectedDealLineItems,
            lineItemIdsWithDeselectedRemarks: selection.lineItemIdsWithDeselectedRemarks,
            processingType: 'MANUAL',
            defaultTaxClassId: taxInfo.defaultTaxClass,
            organizations: deal.organizations,
        });
    }, [selectedDealLineItems, selection.lineItemIdsWithDeselectedRemarks, taxInfo.defaultTaxClass, deal]);

    const businessCase = useMemo((): BusinessCase | null => {
        if (productConfigurations.length === 0 || !selection.mainPricingStrategy) {
            return null;
        }

        return {
            productType: productConfigurations[0].productType,
            pricingStrategy: selection.mainPricingStrategy,
            isStandalone: !(
                productConfigurations.find(({ category }) => category === 'MATERIAL') &&
                productConfigurations.find(({ category }) => category === 'TRANSPORT')
            ),
        };
    }, [productConfigurations, selection.mainPricingStrategy]);

    if (!customerOrganization || !constructionProject || !deal) {
        return;
    }

    return (
        <div className="flex flex-col items-center gap-12 px-6">
            <section className="bg-surface w-full max-w-[720px] rounded p-4">
                <SectionSummary title={t('sections.clientInformation.title')}>
                    <ClientInformationSummary
                        organizationId={customerOrganization.id}
                        constructionProjectId={constructionProjectId}
                        manualCostCenter={constructionProject.costCenter}
                    />
                </SectionSummary>
            </section>

            <section className="pb-18 mb-[108px] flex w-full max-w-7xl flex-col items-center gap-6">
                <div className="w-full max-w-[720px]">
                    <h2 className="font-headline-lg">{t('sections.productGroupSelection.title')}</h2>
                    <MessageBox type="info" className="mt-2">
                        {t('salesforceDealImporter.hintMessage')}
                    </MessageBox>
                </div>

                {deal.deal.lineItems.length ? (
                    <DealLineItemsTable
                        dealLineItems={deal.deal.lineItems}
                        organizations={deal.organizations}
                        onSelectionChange={setSelection}
                    />
                ) : (
                    <aside className="font-copy-md text-subdued max-w-[720px] py-8 text-center">
                        {t('salesforceDealImporter.noWonLineItemsMessage')}
                    </aside>
                )}
            </section>

            <footer className="bg border-t-divider shadow-high fixed inset-x-0 bottom-0 z-20 flex h-[108px] items-center justify-center px-6">
                <div className="flex w-full max-w-[720px] items-center justify-between">
                    <div>{businessCase && <BusinessCasePreview businessCase={businessCase} />}</div>
                    <Button
                        label={t('salesforceDealImporter.nextButton')}
                        size="sm"
                        onClick={() => {
                            void navigate(
                                `/position?projectNumber=${constructionProject.projectCode}&marketCode=${marketCode}`,
                                {
                                    state: {
                                        productConfigurations,
                                        salesforceOpportunityId: opportunityId,
                                        businessCase,
                                    },
                                }
                            );
                        }}
                        disabled={!businessCase}
                    />
                </div>
            </footer>
        </div>
    );
}
